import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import api from '@/plugins/axios'


// ------------------------------
// Width clamping
// ------------------------------

const MIN = 280;
const MAX = 960;

const clamp = (n: number) => Math.min(Math.max(n, MIN), MAX);

// ------------------------------
// Types
// ------------------------------

// Die Index-Signaturen tragen `unknown`, nicht `any`. Der Unterschied ist
// nicht kosmetisch: mit `any` liefert jeder Feldzugriff, den niemand deklariert
// hat, stillschweigend einen brauchbaren Wert, und `this.isAdmin =
// user.is_staff` fiel niemandem auf, obwohl `isAdmin` ein `boolean` ist und
// dort alles Moegliche ankommen konnte. Mit `unknown` muss, wer ein Feld
// wirklich braucht, es hinschreiben — und genau das ist unten fuer `User`
// geschehen.
export interface Project { id?: number; name?: string; [key: string]: unknown }

// Anders als bei `Project` ist die Feldliste hier abschliessend bekannt: sie
// steht in `EmailUserSerializer.Meta.fields` (earns/users/serializers.py).
// `password` fehlt bewusst — das Feld ist write_only und kommt nie zurueck.
export interface User {
  id?: number
  last_login?: string | null
  email?: string
  first_name?: string
  last_name?: string
  is_active?: boolean
  is_staff?: boolean
  forget_password?: boolean
  notification?: string | null
  [key: string]: unknown
}

// `group` ist am Modell `null=True, default=None` (earns/actions/models.py:63),
// eine Massnahme ohne Gruppe kommt also als `null` an. Das gehoert in den Typ:
// `groupActionsMap` wird ueber `a.group !== null` aufgebaut und traegt seit der
// Korrektur am Gruppenhaken die Anzeige, nicht mehr nur einen Zusatzhinweis.
export interface Action  { id: number; name?: string; checked?: boolean; group: number | null; [key: string]: unknown }
export interface ActionGroup { id: number; name?: string; checked?: boolean; group: number; [key: string]: unknown }


// ------------------------------
// Store
// ------------------------------
export const useProjectStore = defineStore('project', {
  state: () => ({
    currentProject: null as Project | null,
    currentProjectActions: [] as Action[],
    currentProjectActionGroups: [] as ActionGroup[],
    allProjects: {} as Record<number, Project>,

    user: null as User | null,
    title: null as string | null,
    navigationMini: false,
    forgotPassword: false,
    isAdmin: false,

    themeMode: 'system' as 'light' | 'dark' | 'system',

    sidebarShown: false,
    sidebarWidth: 700,
    helpComponent: null,
    genColors: {},



    // ------------------------------
    // Colors for generators
    // ------------------------------
    assignGenColors() {
      const forbiddenColor = '#ffffff';
      // Track already-used colors to avoid duplicates
      const usedColors = new Set(Object.values(this.genColors || {}));

      for (const action of this.currentProjectActions || []) {
        if (action?.method_type === 'generator') {
          if (!(action.id in (this.genColors || {}))) {
            let color: string;
            do {
              const r = Math.floor(Math.random() * 0xffffff);
              color = `#${r.toString(16).padStart(6, '0')}`;
            } while (color.toLowerCase() === forbiddenColor || usedColors.has(color));

            // Set once; no mutation method needed in Pinia
            this.genColors = { ...this.genColors, [action.id]: color };
            usedColors.add(color);
          }
        }
      }
    },


    // --------------------------
    // Checkbox
    // --------------------------
    forCalc: [] as number[],        // action IDs where checked === true
    forGroupCalc: [] as number[],
    _forCalcSet: new Set<number>(),
    _forGroupCalcSet: new Set<number>(),
    groupActionsMap: {} as Record<number, number[]>,  // groupId -> [actionId, ...]
    loading: false as boolean,

    // auth readiness (optional but recommended)
    ready: false,
    _initPromise: null as Promise<void> | null,
  }),

  getters: {
    isLoggedIn: (state): boolean => !!state.user,
    userId: (state): number | null => state.user?.id ?? null,
    getNavigationMini: (state): boolean => state.navigationMini,
    getForgotPassword: (state): boolean => state.forgotPassword,

    // --------------------------
    // Checkbox
    // --------------------------

    isActionChecked(state) {
      return (actionId?: number | null) => !!actionId && state._forCalcSet.has(actionId);
    },
    /**
     * Der Anzeigezustand des Gruppen-Hakens — abgeleitet aus den Massnahmen
     * der Gruppe, nicht aus dem gespeicherten `ActionGroup.checked`.
     *
     * Das ist der Kern einer Fehlerbehebung, keine Geschmacksfrage. Gerechnet
     * wird ausschliesslich nach den Massnahmen: das Backend filtert mit
     * `project.actions.filter(checked=True)` (earns/api/views.py:962) und
     * fragt das Gruppen-Flag an keiner Stelle ab. Die beiden koennen
     * auseinanderlaufen, und sie tun es schon beim Anlegen — `Action.checked`
     * hat den Vorgabewert `True`, `ActionGroup.checked` den Vorgabewert
     * `False` (earns/actions/models.py:48 bzw. 221).
     *
     * Sichtbar wurde das so: eine frische Gruppe mit frischen Massnahmen zeigte
     * ein leeres Kaestchen, waehrend jede Massnahme darin gerechnet wurde. Die
     * Massnahmen einer Gruppe haben in der Uebersicht kein eigenes Kaestchen
     * (`v-if="!action.group"`), also war der Gruppenhaken die einzige Anzeige —
     * und er log. Wer ihn daraufhin an- und wieder ausschaltete, setzte ueber
     * den Sammelendpunkt alle Kinder auf `false`, womit plötzlich gar nichts
     * mehr gerechnet wurde.
     *
     * Eine Gruppe ohne Massnahmen hat nichts, woraus sich etwas ableiten
     * liesse; dort bleibt das gespeicherte Flag die Anzeige.
     */
    isGroupChecked(state) {
      return (groupId?: number | null) => {
        if (!groupId) return false;
        const ids = state.groupActionsMap[groupId] || [];
        if (ids.length === 0) return state._forGroupCalcSet.has(groupId);
        return ids.every(id => state._forCalcSet.has(id));
      };
    },

    /**
     * Teilauswahl: einige Massnahmen der Gruppe sind gewaehlt, andere nicht.
     * Vuetify zeigt dafuer den Strich statt des Hakens.
     */
    isGroupIndeterminate(state) {
      return (groupId?: number | null) => {
        if (!groupId) return false;
        const ids = state.groupActionsMap[groupId] || [];
        if (ids.length === 0) return false;
        const gewaehlt = ids.filter(id => state._forCalcSet.has(id)).length;
        return gewaehlt > 0 && gewaehlt < ids.length;
      };
    },

  },

  actions: {
    // --------------------------
    // Boot auth once (optional but fixes refresh/race conditions)
    // --------------------------
    async init() {
      if (this.ready) return
      if (this._initPromise) return this._initPromise
      this._initPromise = (async () => {
        await this.checkSession()
        this.ready = true
      })().finally(() => {
        this._initPromise = null
      })
      return this._initPromise
    },

    // --------------------------
    // Session
    // --------------------------
    async checkSession() {
      try {
        const response = await api.get<User>('/auth/self/')
        this.user = response.data
        this.isAdmin = this.user?.is_staff || false
      } catch {
        // Do not throw; just mark logged out.
        this.user = null
        this.isAdmin = false
      }
    },

    // --------------------------
    // Auth
    // --------------------------
    async login(data: { email: string; password: string }) {
      try {
        const response = await api.post<User & { forget_password: boolean }>(
        '/auth/login/',
        data
      )

      // Backend has authenticated (normal or OTP). Respect the forced-change fla
      this.forgotPassword = response.data.forget_password

     
      // Always set the user after a successful authentication, even for OTP flows.
      // This makes `isLoggedIn` true while we still gate access via `forgotPassword`.
      const { forget_password, ...user } = response.data
      this.user = user
      this.isAdmin = this.user?.is_staff || false
      
      return response.data
      } catch (error) {
        console.error('Login nicht erfolgreich.', error)

        throw error
        
      }
      
    },

    async logout() {
      try {
        await api.post('/auth/logout/', null)

        this.reset()

      } catch (error) {
        console.error('Logout nicht erfolgreich.', error)
      }
      
    },

    // --------------------------
    // Data fetches
    // --------------------------
    async fetchAllProjects() {
      try {
        const response = await api.get<Project[]>('/projects/')
        this.allProjects = response.data.reduce((res, project) => {
          if (project.id !== undefined) res[project.id] = project
          return res
        }, {} as Record<number, Project>)
      } catch (error) {
        console.error('Error fetching projects:', error)
      }
    },

    async fetchCurrentProject(projectId: number) {
      try {
        const [projectRes, actionsRes, groupsRes] = await Promise.all([
          api.get<Project>(`/projects/${projectId}/`),
          api.get<Action[]>('/actions/', { params: { project: projectId } }),
          api.get<ActionGroup[]>('/actiongroups/', { params: { project: projectId } }),
        ])
        this.currentProject = projectRes.data;
        this.currentProjectActions = actionsRes.data as Action[];
        this.assignGenColors();
        this.currentProjectActionGroups = groupsRes.data as ActionGroup[];

        // Fill Sets from server truth
        this._forCalcSet = new Set(
          this.currentProjectActions.filter(a => a.checked === true).map(a => a.id),
        );
        this._forGroupCalcSet = new Set(
          this.currentProjectActionGroups?.filter(g => g.checked === true).map(g => g.id),
        );

        // Build mapping for fast group ops
        this._rebuildGroupActionsMap();
        this._syncArraysFromSets();

      } catch (error) {
        console.error('Error fetching project data:', error)
      }
    },


    // --------------------------
    // UI bits
    // --------------------------
    setNavigationMini(mini: boolean) {
      this.navigationMini = mini
    },

    setTitle(title: string | null) {
      this.title = title
    },

    resetTitle() {
      this.title = null
    },

    // --------------------------
    // Theme
    // --------------------------
    loadThemeMode() {
      try {
        const stored = localStorage.getItem('themeMode')

        if (stored === 'light' || stored === 'dark' || stored === 'system') {
          this.themeMode = stored
        } else {
          this.themeMode = 'system'
        }
      } catch {
        this.themeMode = 'system'
      }
    },

    setThemeMode(mode: 'light' | 'dark' | 'system') {
      this.themeMode = mode

      try {
        localStorage.setItem('themeMode', mode)
      } catch {}
    },

    cycleThemeMode() {
      if (this.themeMode === 'system') {
        this.setThemeMode('light')
      } else if (this.themeMode === 'light') {
        this.setThemeMode('dark')
      } else {
        this.setThemeMode('system')
      }
    },


    // --------------------------
    // Sidebar
    // --------------------------
    showSidebar(component = null) {    // not really needed if toggleSidebar is used
      this.helpComponent = component ? markRaw(component) : null
      this.sidebarShown = true
    },

    hideSidebar() {   // used in main.ts
      this.sidebarShown = false
      this.helpComponent = null
    },
    
    setSidebarWidth(width: number) {
      this.sidebarWidth = clamp(width);
      try {
        localStorage.setItem('sidebarWidth', String(this.sidebarWidth));
      } catch {}
    },
    
    loadSidebarWidth() {
      try {
        const raw = localStorage.getItem('sidebarWidth');
        if (raw) this.sidebarWidth = clamp(Number(raw));
      } catch {}
    },
    
    increaseSidebarWidth(step = 20) {
      this.setSidebarWidth(this.sidebarWidth + step);
    },

    decreaseSidebarWidth(step = 20) {
      this.setSidebarWidth(this.sidebarWidth - step);
    },

    
    setHelpComponent(component = null) {
    this.helpComponent = component ? markRaw(component) : null;
    },



    // --------------------------
    // Checkbox
    // --------------------------
    _rebuildGroupActionsMap() {
      const map: Record<number, number[]> = {};
      for (const a of this.currentProjectActions) {
        if (a.group !== null) {
          (map[a.group] ||= []).push(a.id);
        }
      }
      this.groupActionsMap = map;
    },

    // Keep arrays in sync with Sets (what you’ll actually use elsewhere)
    _syncArraysFromSets() {
      this.forCalc = Array.from(this._forCalcSet.values());
      this.forGroupCalc = Array.from(this._forGroupCalcSet.values());
    },
    
    // Toggle single action: optimistic update + rollback on error
    async setActionChecked(actionId: number, checked: boolean) {
      const had = this._forCalcSet.has(actionId);

      // Optimistic change
      if (checked) this._forCalcSet.add(actionId);
      else this._forCalcSet.delete(actionId);
      this._syncArraysFromSets();

      try {
        await api.post(`/actions/${actionId}/set-checked/`, { checked });
      } catch (err) {
        // Rollback
        if (had) this._forCalcSet.add(actionId);
        else this._forCalcSet.delete(actionId);
        this._syncArraysFromSets();
        throw err;
      }
    },

    // Toggle group: call batch endpoint; also update children in forCalc
    async setGroupChecked(groupId: number, checked: boolean) {
      const prevGroupChecked = this._forGroupCalcSet.has(groupId);
      const childIds = this.groupActionsMap[groupId] || [];
      const prevChildStates = childIds.map(id => [id, this._forCalcSet.has(id)] as const);

      // Optimistic update (group + children)
      if (checked) {
        this._forGroupCalcSet.add(groupId);
        childIds.forEach(id => this._forCalcSet.add(id));
      } else {
        this._forGroupCalcSet.delete(groupId);
        childIds.forEach(id => this._forCalcSet.delete(id));
      }
      this._syncArraysFromSets();

      try {
        // Backend source of truth: your group endpoint
        await api.post(`/actiongroups/${groupId}/set-checked/`, { checked });
      } catch (err) {
        // Rollback both group and children
        if (prevGroupChecked) this._forGroupCalcSet.add(groupId);
        else this._forGroupCalcSet.delete(groupId);
        prevChildStates.forEach(([id, was]) => {
          if (was) this._forCalcSet.add(id);
          else this._forCalcSet.delete(id);
        });
        this._syncArraysFromSets();
        throw err;
      }
    },

    
    // --------------------------
    // Optional: reset on logout
    // --------------------------
    reset() {
      this.currentProject = null
      this.currentProjectActions = []
      this.currentProjectActionGroups = []
      this.allProjects = {}
      this.title = null
      this.navigationMini = false
      this.sidebarShown = false
      this.sidebarWidth = 700
      this.helpComponent = null
      this.forCalc = []
      this.forGroupCalc = []
      this._forCalcSet = new Set()
      this._forGroupCalcSet = new Set()
      this.groupActionsMap = {}
      this.loading = false
      this.user = null
      this.isAdmin = false
      this.forgotPassword = false
      this.ready = false
      this._initPromise = null
    },



  },
})